/**
 * WMS Page DSL — Declarative page definitions for all WMS screens.
 *
 * Instead of hardcoding columns, forms, actions, and status badges in JSX,
 * each WMS page is described as a pure data structure that the generic
 * renderer (<WmsPageRenderer />) interprets.
 *
 * Benefits:
 *  - Add/remove/reorder columns by editing JSON, not JSX
 *  - Status colors, KPI cards, action buttons — all config
 *  - Tenant-level overrides possible (load from API later)
 *  - Mobile vs desktop layout auto-derived from same DSL
 */

// ─── Primitives ────────────────────────────────────────────────────────────

export interface StatusDef {
  key: string;
  label: string;
  color: string;           // tailwind class e.g. 'bg-green-100 text-green-700'
  icon?: string;            // lucide icon name
}

export interface ColumnDef {
  field: string;
  label: string;
  width?: number;
  format?: 'text' | 'date' | 'datetime' | 'number' | 'currency' | 'status' | 'progress' | 'badge';
  sortable?: boolean;
  hidden?: boolean;
  /** When true and tab has detailApiPath, clicking the cell opens detail dialog. */
  openDetailOnClick?: boolean;
  /** Open an authenticated API resource from this cell. */
  openApiPath?: string;
  /** Render opened API text as an in-app CSV table instead of browser blob. */
  openAs?: 'csvViewer' | 'source';
  /** Map API field to display. If absent, uses `field` directly */
  accessor?: string;
}

export interface KpiCardDef {
  key: string;
  label: string;
  icon: string;             // lucide icon name
  color?: string;           // tailwind text color
  /** JS expression path on the fetched data, e.g. 'counts.skus' */
  valuePath: string;
  /** Navigation link when clicked */
  link?: string;
}

export interface FilterDef {
  field: string;
  label: string;
  type: 'select' | 'multiselect' | 'search' | 'date' | 'daterange';
  options?: Array<{ label: string; value: string }>;
  /** Load options from an API endpoint */
  optionsEndpoint?: string;
  /** Map API item to { label, value } */
  optionsMap?: { label: string; value: string };
  /** If 'fromData', options are derived from fetched data */
  optionsFrom?: string;
}

export interface FormFieldDef {
  field: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'multiselect' | 'textarea' | 'date' | 'hidden';
  /** Desktop column span when rendered in a grid layout. */
  span?: number;
  required?: boolean;
  placeholder?: string;
  options?: Array<{ label: string; value: string }>;
  /** Load options from an API endpoint */
  optionsEndpoint?: string;
  /** When false, do not preload options on dialog open; rely on remote search instead. */
  preloadOptions?: boolean;
  /** Map API item to { label, value } */
  optionsMap?: { label: string; value: string };
  defaultValue?: string | string[];
  /** API payload key (camelCase). When set, the form uses `field` for state but sends `apiField` in the request body. */
  apiField?: string;
  /** Conditionally show this field when another field matches one of these values. */
  showWhen?: {
    field: string;
    values: string[];
  };
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    message?: string;
  };
}

export interface ActionDef {
  key: string;
  label: string;
  icon?: string;
  variant?: 'default' | 'destructive' | 'outline' | 'ghost';
  /** Optional navigation route (use :id for row id). If set, no API call is required. */
  route?: string;
  /** Open a record detail API response in the current page right-side sheet. Supports :id and :field placeholders. */
  detailApiPath?: string;
  /** Open a purpose-built document panel in the current page right-side sheet. */
  detailPanel?: 'purchaseOrder' | 'materialRequest' | 'issueOrder' | 'returnForm' | 'projectReturn' | 'inboundOrder' | 'receivingStickers' | 'qcRecord' | 'qcGroup' | 'cycleCount';
  /** Optional row field used as the document id for detailPanel. Defaults to id/order_id. */
  detailIdField?: string;
  /** Optional title for the right-side detail sheet. */
  detailTitle?: string;
  /** Open an authenticated API resource in a new browser tab (supports :id and :field placeholders). */
  openApiPath?: string;
  /** Render opened API text as an in-app CSV table instead of browser blob. */
  openAs?: 'csvViewer' | 'source';
  /** API call: method + path (use :id for row id) */
  api?: { method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'; path: string; body?: Record<string, unknown> };
  /** Only show when row status matches one of these */
  showWhen?: string[];
  /** Confirmation message before executing */
  confirm?: string;
  /** Transitions status to this value (client-side optimistic) */
  toStatus?: string;
  /** Refresh page data after the action succeeds. */
  refetch?: boolean;
  /** Role requirement */
  roles?: string[];
}

export interface BatchActionDef {
  key: string;
  label: string;
  icon?: string;
  variant?: 'default' | 'destructive' | 'outline' | 'ghost';
  /** Batch action currently supports API execution against each selected row id. */
  api: { method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'; path: string; body?: Record<string, unknown> };
  /** Confirmation message before executing against selected rows. */
  confirm?: string;
  /** Optional optimistic target status. */
  toStatus?: string;
}

export interface DialogDef {
  key: string;
  title: string;
  description?: string;
  /** Optional navigation route. If set, top-level create button navigates instead of opening dialog. */
  route?: string;
  fields: FormFieldDef[];
  /** Desktop column count for the dialog form. Defaults to 6. */
  layoutColumns?: number;
  submitLabel?: string;
  api: { method: 'POST' | 'PUT'; path: string };
  /** After success, refetch list */
  refetch?: boolean;
}

export interface PageActionDef {
  key: string;
  label: string;
  icon?: string;
  variant?: 'default' | 'destructive' | 'outline' | 'ghost';
  route?: string;
  dialogKey?: string;
}

export type WmsImportType = 'skus' | 'asn-lines' | 'purchase-orders';

export interface ImportWorkflowDef {
  /** Import workflow executed by the renderer. */
  type: WmsImportType;
  /** Top-right button label. Defaults from type when omitted. */
  buttonLabel?: string;
  /** Tab to select before opening the import sheet. */
  openTab?: string;
  /** Tab to select after successful import. */
  successTab?: string;
}

export interface TabDef {
  key: string;
  label: string;
  icon?: string;
  /** Optional page-level create dialog key(s) resolved from the active tab. */
  createDialogKey?: string;
  createDialogKeys?: string[];
  /** API endpoint to fetch tab data */
  apiEndpoint: string;
  /** Query params passed to API */
  apiParams?: Record<string, string | number>;
  /** Table pagination behavior. Defaults to auto: show when row count exceeds the smallest page size. */
  pagination?: { mode?: 'auto' | 'always'; pageSize?: number };
  /** Map API response to rows array */
  dataPath?: string;
  columns: ColumnDef[];
  /** Row actions (buttons per row) */
  rowActions?: ActionDef[];
  /** Batch actions executed against the current selected rows. */
  batchActions?: BatchActionDef[];
  /** Row action button presentation. */
  rowActionStyle?: 'button' | 'icon';
  /** Where to place the actions column. */
  actionColumnPosition?: 'start' | 'end';
  /** Freeze the actions column while the grid scrolls. */
  freezeActionColumn?: boolean;
  /** Detail API for row drill-down (use :id for row id) */
  detailApiPath?: string;
  /** Optional custom detail panel for row drill-down. */
  detailPanel?: 'purchaseOrder' | 'materialRequest' | 'issueOrder' | 'returnForm' | 'projectReturn' | 'inboundOrder' | 'receivingStickers' | 'qcRecord' | 'qcGroup' | 'cycleCount';
  /** How record detail is shown */
  detailMode?: 'dialog' | 'inline' | 'both';
  /** Optional detail dialog title */
  detailTitle?: string;
  /** Inline edit / open dialog edit behavior for simple master-data records */
  editMode?: 'none' | 'inline' | 'dialog' | 'both';
  /** Update API for record edit (use :id for row id) */
  updateApi?: { method: 'PATCH' | 'PUT'; path: string };
  /** Editable fields for inline/dialog edit */
  editFields?: FormFieldDef[];
  /** API for adding child lines (use :id for parent row id) */
  lineAddApi?: { method: 'POST'; path: string };
  /** Fields for the add-line form */
  lineFields?: FormFieldDef[];
  /** API for updating child lines (use :lineId for child line id) */
  lineUpdateApi?: { method: 'PATCH' | 'PUT'; path: string };
  /** Editable fields for child lines */
  lineEditFields?: FormFieldDef[];
  /** API for deleting child lines (use :lineId for child line id) */
  lineDeleteApi?: { method: 'DELETE'; path: string };
  /** API for deleting the record itself (use :id for row id) */
  deleteApi?: { method: 'DELETE'; path: string };
  /** Status field name for badge rendering */
  statusField?: string;
  /** Empty state message */
  emptyMessage?: string;
  /** Optional attachment reference type for detail panel upload/list. */
  attachmentReferenceType?: string;
  /** Optional attachment section title. */
  attachmentTitle?: string;
  /** KPI cards above the table */
  kpiCards?: KpiCardDef[];
  /** Hide table footer row with page row count and numeric totals. */
  hideFooter?: boolean;
  /** XLSX export endpoint (e.g. /wms/reports/pipe-balance-list/export). When set, an Export XLSX button appears in the tab toolbar. */
  exportEndpoint?: string;
  /** Optional legacy XLSX export using Wasco-style control layout. */
  legacyExportEndpoint?: string;
}

/** Top-level page definition */
export interface WmsPageDef {
  key: string;
  title: string;
  icon: string;
  /** Global KPI cards (show above tabs) */
  kpiCards?: KpiCardDef[];
  /** Tabs */
  tabs: TabDef[];
  /** Page-level dialogs (create forms, etc.) */
  dialogs?: DialogDef[];
  /** Compact operational list chrome: narrow header, inline filters, no KPI blocks. */
  compactList?: boolean;
  /** Hide runtime/template badges for operational WP list pages. */
  hideRuntimeBadges?: boolean;
  /** Hide page-level and tab-level KPI cards. */
  hideKpiCards?: boolean;
  /** Hide generic keyword search box when page already has dedicated filters. */
  hideSearchBox?: boolean;
  /** Filter area column count for WP operational pages. Defaults to 4. */
  filterLayoutColumns?: 4 | 6;
  /** Hide the generic Add button/chooser. Use primaryActions for direct actions. */
  hideAddButton?: boolean;
  /** Direct top-right page actions. */
  primaryActions?: PageActionDef[];
  /** Optional import workflow. Kept in DSL so renderer does not infer by page key. */
  importWorkflow?: ImportWorkflowDef;
  /** Status definitions shared across all tabs */
  statuses: StatusDef[];
  /** Filters applied across all tabs */
  filters?: FilterDef[];
  /** Search behavior. Page = one shared search box, tab = each tab owns its own search box/value. */
  searchScope?: 'page' | 'tab';
  /** Optional placeholder override for the renderer search input. */
  searchPlaceholder?: string;
  /** Enable barcode scanning */
  scanEnabled?: boolean;
  /** Refresh interval in ms (0 = manual only) */
  refreshInterval?: number;
}

// ─── Status Maps ───────────────────────────────────────────────────────────

export const WMS_STATUSES: StatusDef[] = [
  // ASN / Inbound
  { key: 'DRAFT',               label: 'Draft',              color: 'bg-gray-100 text-gray-600' },
  { key: 'SUBMITTED',           label: 'Submitted',          color: 'bg-blue-100 text-blue-700' },
  { key: 'PARTIALLY_RECEIVED',  label: 'Partial Received',   color: 'bg-yellow-100 text-yellow-700' },
  { key: 'RECEIVING',           label: 'Receiving',          color: 'bg-yellow-100 text-yellow-700' },
  { key: 'RECEIVED',            label: 'Received',           color: 'bg-green-100 text-green-700' },
  { key: 'QC_PENDING',          label: 'QC Pending',         color: 'bg-orange-100 text-orange-700' },
  { key: 'QC_PASSED',           label: 'QC Passed',          color: 'bg-green-100 text-green-700' },
  { key: 'QC_PARTIAL',          label: 'QC Partial',         color: 'bg-amber-100 text-amber-700' },
  { key: 'QC_FAILED',           label: 'QC Failed',          color: 'bg-red-100 text-red-600' },
  { key: 'PASSED',              label: 'Passed',             color: 'bg-green-100 text-green-700' },
  { key: 'PARTIAL',             label: 'Partial',            color: 'bg-amber-100 text-amber-700' },
  { key: 'FAILED',              label: 'Failed',             color: 'bg-red-100 text-red-600' },
  { key: 'PUTAWAY_PENDING',     label: 'Putaway Pending',    color: 'bg-purple-100 text-purple-700' },
  { key: 'REJECTED',            label: 'Rejected',           color: 'bg-red-100 text-red-600' },
  { key: 'CANCELLED',           label: 'Cancelled',          color: 'bg-red-100 text-red-600' },
  // Outbound
  { key: 'NEW',                 label: 'New',                color: 'bg-gray-100 text-gray-600' },
  { key: 'ALLOCATED',           label: 'Allocated',          color: 'bg-blue-100 text-blue-700' },
  { key: 'PICKING',             label: 'Picking',            color: 'bg-yellow-100 text-yellow-700' },
  { key: 'PACKED',              label: 'Packed',             color: 'bg-orange-100 text-orange-700' },
  { key: 'SHIPPED',             label: 'Shipped',            color: 'bg-green-100 text-green-700' },
  // Work Orders
  { key: 'PENDING',             label: 'Pending',            color: 'bg-gray-100 text-gray-600' },
  { key: 'ASSIGNED',            label: 'Assigned',           color: 'bg-blue-100 text-blue-700' },
  { key: 'PLANNED',             label: 'Planned',            color: 'bg-slate-100 text-slate-700' },
  { key: 'IN_PROGRESS',         label: 'In Progress',        color: 'bg-yellow-100 text-yellow-700' },
  { key: 'REVIEWING',           label: 'Reviewing',          color: 'bg-amber-100 text-amber-700' },
  { key: 'PENDING_APPROVAL',    label: 'Pending Approval',   color: 'bg-amber-100 text-amber-700' },
  { key: 'APPROVED',            label: 'Approved',           color: 'bg-cyan-100 text-cyan-700' },
  { key: 'COMPLETED',           label: 'Completed',          color: 'bg-green-100 text-green-700' },
  { key: 'ON_HOLD',             label: 'On Hold',            color: 'bg-red-100 text-red-600' },
  // Inventory
  { key: 'ACTIVE',              label: 'Active',             color: 'bg-green-100 text-green-700' },
  { key: 'INACTIVE',            label: 'Inactive',           color: 'bg-gray-100 text-gray-400' },
  { key: 'QUARANTINE',          label: 'Quarantine',         color: 'bg-red-100 text-red-600' },
  // Transaction types
  { key: 'INBOUND',             label: 'Inbound',            color: 'bg-green-100 text-green-700' },
  { key: 'QC',                  label: 'QC',                 color: 'bg-blue-100 text-blue-700' },
  { key: 'PUTAWAY',             label: 'Putaway',            color: 'bg-purple-100 text-purple-700' },
  { key: 'PICK',                label: 'Pick',               color: 'bg-orange-100 text-orange-700' },
  { key: 'SHIP',                label: 'Ship',               color: 'bg-green-100 text-green-700' },
  { key: 'ADJUSTMENT',          label: 'Adjustment',         color: 'bg-yellow-100 text-yellow-700' },
  { key: 'TRANSFER',            label: 'Transfer',           color: 'bg-indigo-100 text-indigo-700' },
];

// ─── Helper ────────────────────────────────────────────────────────────────

export function getStatusDef(key: string, defs: StatusDef[]): StatusDef {
  return defs.find(s => s.key === key) ?? { key, label: key, color: 'bg-gray-100 text-gray-600' };
}
